import {chunk} from '@benchling/js-utils';
import React from 'react';

import {PersistedDataFrameGraphQL} from 'data-frames/wrapper/PersistedDataFrameTableWrapper';
import getTestProps from 'test/lib/cypress/getTestProps';
import Col from 'ui-library/bs/Col';
import FormGroup from 'ui-library/bs/FormGroup';
import Row from 'ui-library/bs/Row';
import FieldLabel from 'ui-library/inputs/FieldLabel';
import Modal from 'ui-library/modals/Modal';
import {readableTime} from 'util/DateUtil';
import fuzzydate from 'util/fuzzydate';
import {capitalize, formatBytes} from 'util/NameUtil';

export interface PersistedDataFrameMetadataModalProps {
  persistedDataFrame: PersistedDataFrameGraphQL;
  /**
   * Additional data to show in the grid of metadata. Each entry will get half of a row (or a full row
   * on small screens).
   *
   * @note With current usage, React doesn't seem to mind if each element doesn't have a `key` prop.
   *
   * @example
   * ```
   * [
   *   {
   *     component: (
   *       <FormGroup>
   *         <FieldLabel label="Creator" />
   *         <div>{persistedDataFrame.creator.name}</div>
   *       </FormGroup>
   *     ),
   *     key: 'Creator',
   *   },
   * ]
   * ```
   */
  additionalGridCells?: Array<{component: React.ReactNode; label: string}>;
  /**
   * Additional element to show underneath the grid of metadata.
   *
   * @example <pre>{sql query that created the dataframe}</pre>
   */
  additionalContent?: JSX.Element;
  onRequestClose: () => void;
  onRequestSubmit: () => void;
}

export default function PersistedDataFrameMetadataModal({
  persistedDataFrame,
  additionalGridCells,
  additionalContent,
  onRequestClose,
}: PersistedDataFrameMetadataModalProps): JSX.Element {
  return (
    <Modal
      mediumLarge={true}
      title="Table metadata"
      onRequestClose={onRequestClose}
      dataTestAttrs={{component: 'PersistedDataFrameMetadataModal', element: 'root'}}
    >
      <SharedDataFrameMetadataContents persistedDataFrame={persistedDataFrame} />
      <AdditionalDataFrameMetadataContents
        additionalGridCells={additionalGridCells}
        additionalContent={additionalContent}
      />
    </Modal>
  );
}

function SharedDataFrameMetadataContents({
  persistedDataFrame,
}: Pick<PersistedDataFrameMetadataModalProps, 'persistedDataFrame'>): JSX.Element {
  return (
    <>
      <Row>
        <Col
          sm={6}
          dataTestAttrs={{component: 'PersistedDataFrameMetadataModal', element: 'column', key: 'Name'}}
        >
          <FormGroup>
            <FieldLabel label="Name" />
            <div
              {...getTestProps({component: 'PersistedDataFrameMetadataModal', element: 'value', key: 'Name'})}
            >
              {persistedDataFrame.name}
            </div>
          </FormGroup>
        </Col>
        <Col
          sm={6}
          dataTestAttrs={{component: 'PersistedDataFrameMetadataModal', element: 'column', key: 'Creator'}}
        >
          <FormGroup>
            <FieldLabel label="Creator" />
            <div
              {...getTestProps({
                component: 'PersistedDataFrameMetadataModal',
                element: 'value',
                key: 'Creator',
              })}
            >
              {persistedDataFrame.creator.name}
            </div>
          </FormGroup>
        </Col>
      </Row>
      <Row>
        <Col
          sm={6}
          dataTestAttrs={{
            component: 'PersistedDataFrameMetadataModal',
            element: 'column',
            key: 'Last modified',
          }}
        >
          <FormGroup>
            <FieldLabel label="Last modified" />
            <div
              {...getTestProps({
                component: 'PersistedDataFrameMetadataModal',
                element: 'value',
                key: 'Last modified',
              })}
            >
              {capitalize(fuzzydate(persistedDataFrame.modifiedAt))}
            </div>
          </FormGroup>
        </Col>
        <Col
          sm={6}
          dataTestAttrs={{
            component: 'PersistedDataFrameMetadataModal',
            element: 'column',
            key: 'Created at',
          }}
        >
          <FormGroup>
            <FieldLabel label="Created at" />
            <div
              {...getTestProps({
                component: 'PersistedDataFrameMetadataModal',
                element: 'value',
                key: 'Created at',
              })}
            >
              {readableTime(persistedDataFrame.createdAt)}
            </div>
          </FormGroup>
        </Col>
      </Row>
      <Row>
        <Col
          sm={6}
          dataTestAttrs={{
            component: 'PersistedDataFrameMetadataModal',
            element: 'column',
            key: 'Rows',
          }}
        >
          <FormGroup>
            <FieldLabel label="Rows" />
            <div
              {...getTestProps({component: 'PersistedDataFrameMetadataModal', element: 'value', key: 'Rows'})}
            >
              {persistedDataFrame.numRows}
            </div>
          </FormGroup>
        </Col>
        <Col
          sm={6}
          dataTestAttrs={{
            component: 'PersistedDataFrameMetadataModal',
            element: 'column',
            key: 'Size',
          }}
        >
          <FormGroup>
            <FieldLabel label="Size" />
            <div
              {...getTestProps({component: 'PersistedDataFrameMetadataModal', element: 'value', key: 'Size'})}
            >
              {persistedDataFrame.size ? formatBytes(persistedDataFrame.size) : 'N/A'}
            </div>
          </FormGroup>
        </Col>
      </Row>
    </>
  );
}

export function AdditionalDataFrameMetadataContents({
  additionalGridCells = [],
  additionalContent,
}: Pick<PersistedDataFrameMetadataModalProps, 'additionalGridCells' | 'additionalContent'>): JSX.Element {
  return (
    <>
      {[...chunk(additionalGridCells, 2)].map((rowCells, rowIndex) => (
        <Row key={rowIndex}>
          {rowCells.map(({label, component}, colIndex) => (
            <Col
              sm={6}
              key={`${label}-${rowIndex}-${colIndex}`}
              dataTestAttrs={{
                component: 'PersistedDataFrameMetadataModal',
                element: 'column',
                key: label,
              }}
            >
              <FormGroup>
                <FieldLabel label={label} />
                <div
                  {...getTestProps({
                    component: 'PersistedDataFrameMetadataModal',
                    element: 'value',
                    key: label,
                  })}
                >
                  {component}
                </div>
              </FormGroup>
            </Col>
          ))}
        </Row>
      ))}
      <div
        {...getTestProps({
          component: 'PersistedDataFrameMetadataModal',
          element: 'div',
          key: 'additionalContent',
        })}
      >
        {additionalContent}
      </div>
    </>
  );
}
